import { RESEARCH, type ResearchNodeDef } from '../data/research';
import { saveManager } from './SaveManager';
import { achievements } from './AchievementManager';

/**
 * Research tiers are also a command-ladder gate. The lab shows these values
 * before purchase so DATA cannot be spent on an upgrade the commander cannot
 * deploy yet.
 */
export function getResearchRankRequirement(node: Pick<ResearchNodeDef, 'tier'>): number {
  if (node.tier >= 5) return 10;
  if (node.tier >= 4) return 7;
  if (node.tier >= 3) return 5;
  if (node.tier >= 2) return 3;
  return 1;
}

export interface ResearchBonuses {
  /** Multiplicative damage bonus per tower id (1 = no bonus) */
  damageMul: Record<string, number>;
  rangeMul: Record<string, number>;
  rateMul: Record<string, number>;
  armourPenAdd: Record<string, number>;
  critChanceAdd: Record<string, number>;
  splashMul: Record<string, number>;
  chainAdd: Record<string, number>;
  abilityCdMul: Record<string, number>;
  startSupplyBonus: number;
  integrityBonus: number;
  earlyWaveBonusMul: number;
  droneDamageMul: number;
  rollingBombCount: number;
  rollingBombSlowDuration: number;
  rollingBombSnowUnlocked: boolean;
  airstrikeDamageMul: number;
  airstrikeRadiusMul: number;
  airstrikeFireDuration: number;
  airstrikeFireDps: number;
  empDurationMul: number;
  barrierHpMul: number;
  barrierDurationMul: number;
  barrierCostMul: number;
  thermalSplashMul: number;
  baseUpgradeCostMul: number;
  baseDamageMul: number;
  baseRangeMul: number;
  symbiosisDamageMul: number;
  symbiosisUnlocked: Record<string, boolean>;
}

function defaultBonuses(): ResearchBonuses {
  return {
    damageMul: {
      ballistic: 1,
      thermal: 1,
      artillery: 1,
      energy: 1,
      vulcan: 1,
      sabre: 1,
      tesla: 1,
      cryo: 1,
      dronehive: 1,
      drone: 1,
      airstrike: 1,
    },
    rangeMul: { ballistic: 1, thermal: 1, artillery: 1, energy: 1, vulcan: 1, sabre: 1, tesla: 1, cryo: 1, dronehive: 1 },
    rateMul: { ballistic: 1, thermal: 1, artillery: 1, energy: 1, vulcan: 1, sabre: 1, tesla: 1, cryo: 1, dronehive: 1 },
    armourPenAdd: { ballistic: 0, thermal: 0, artillery: 0, energy: 0, vulcan: 0, sabre: 0, tesla: 0, cryo: 0, dronehive: 0 },
    critChanceAdd: { ballistic: 0, thermal: 0, artillery: 0, energy: 0, vulcan: 0, sabre: 0, tesla: 0, cryo: 0, drone: 0, dronehive: 0 },
    splashMul: { ballistic: 1, thermal: 1, artillery: 1, energy: 1, vulcan: 1, sabre: 1, tesla: 1, cryo: 1, dronehive: 1 },
    chainAdd: { tesla: 0 },
    abilityCdMul: { airstrike: 1, barrier: 1, rollingdeath: 1, drone: 1, emp: 1 },
    startSupplyBonus: 0,
    integrityBonus: 0,
    earlyWaveBonusMul: 1,
    droneDamageMul: 1,
    rollingBombCount: 6,
    rollingBombSlowDuration: 0,
    rollingBombSnowUnlocked: false,
    airstrikeDamageMul: 1,
    airstrikeRadiusMul: 1,
    airstrikeFireDuration: 0,
    airstrikeFireDps: 24,
    empDurationMul: 1,
    barrierHpMul: 1,
    barrierDurationMul: 1,
    barrierCostMul: 1,
    thermalSplashMul: 1,
    baseUpgradeCostMul: 1,
    baseDamageMul: 1,
    baseRangeMul: 1,
    symbiosisDamageMul: 1,
    symbiosisUnlocked: {},
  };
}

export class ResearchManager {
  get purchased(): string[] {
    return saveManager.data.research;
  }

  get points(): number {
    return saveManager.data.researchPoints;
  }

  isUnlocked(id: string): boolean {
    return this.purchased.includes(id);
  }

  /** Whether a bolt-on has been licensed in the staged SYMBIOSIS branch. */
  isSymbiosisUnlocked(moduleId: string): boolean {
    if (saveManager.data.settings.devMode) return true;
    return this.bonuses().symbiosisUnlocked[moduleId] === true;
  }

  /**
   * Evolutions use the target platform's tier-one research as their license.
   * This keeps the battle menu and the research tree on the same progression
   * rules while still allowing developer mode to exercise every branch.
   */
  isTowerResearchUnlocked(towerId: string): boolean {
    if (saveManager.data.settings.devMode) return true;
    return RESEARCH.some(
      (node) => node.tier === 1 && node.effect.target === towerId && this.isUnlocked(node.id),
    );
  }

  canUnlock(node: ResearchNodeDef): boolean {
    if (this.isUnlocked(node.id)) return false;
    if (saveManager.data.rank < getResearchRankRequirement(node)) return false;
    if (this.points < node.cost) return false;
    if (node.requires?.length) {
      return node.requires.every((r) => this.isUnlocked(r));
    }
    return true;
  }

  /** Human-readable reasons a node cannot be unlocked right now. */
  lockReasons(node: ResearchNodeDef): string[] {
    if (this.isUnlocked(node.id)) return ['RESEARCHED'];
    const reasons: string[] = [];
    const rank = getResearchRankRequirement(node);
    const currentRank = saveManager.data.rank;
    if (currentRank < rank) {
      reasons.push(`COMMAND RANK ${rank} REQUIRED — YOU ARE RANK ${currentRank}`);
    }
    const missing = (node.requires ?? [])
      .filter((id) => !this.isUnlocked(id))
      .map((id) => RESEARCH.find((candidate) => candidate.id === id)?.name ?? id);
    if (missing.length) reasons.push(`REQUIRES ${missing.join(', ')}`);
    if (this.points < node.cost) {
      reasons.push(`NEED ${node.cost} DATA — YOU HAVE ${this.points}`);
    }
    return reasons;
  }

  /** Short badge text for a locked research card. */
  lockBadge(node: ResearchNodeDef): string {
    if (this.isUnlocked(node.id)) return 'DONE';
    const rank = getResearchRankRequirement(node);
    if (saveManager.data.rank < rank) return `RANK ${rank}`;
    const missing = (node.requires ?? []).filter((id) => !this.isUnlocked(id));
    if (missing.length) return 'PREREQ';
    if (this.points < node.cost) return 'NO DATA';
    return 'LOCKED';
  }

  unlock(id: string): boolean {
    const node = RESEARCH.find((n) => n.id === id);
    if (!node || !this.canUnlock(node)) return false;
    saveManager.adjustResearchPoints(-node.cost);
    saveManager.data.research.push(node.id);
    saveManager.save();
    achievements.noteResearch();
    return true;
  }

  byCategory(): Record<string, ResearchNodeDef[]> {
    const map: Record<string, ResearchNodeDef[]> = {};
    for (const n of RESEARCH) {
      if (!map[n.category]) map[n.category] = [];
      map[n.category].push(n);
    }
    return map;
  }

  bonuses(): ResearchBonuses {
    const b = defaultBonuses();
    for (const id of this.purchased) {
      const n = RESEARCH.find((r) => r.id === id);
      if (!n) continue;
      const t = n.effect.target;
      switch (n.effect.type) {
        case 'damage':
          if (t === 'drone') b.droneDamageMul += n.effect.value;
          else if (t === 'airstrike') b.airstrikeDamageMul += n.effect.value;
          else if (t && t in b.damageMul) b.damageMul[t] += n.effect.value;
          break;
        case 'range':
          if (t && t in b.rangeMul) b.rangeMul[t] += n.effect.value;
          break;
        case 'rate':
          if (t && t in b.rateMul) b.rateMul[t] += n.effect.value;
          break;
        case 'armourPen':
          if (t && t in b.armourPenAdd) b.armourPenAdd[t] += n.effect.value;
          break;
        case 'crit':
          if (t && t in b.critChanceAdd) b.critChanceAdd[t] += n.effect.value;
          break;
        case 'splash':
          if (t && t in b.splashMul) b.splashMul[t] += n.effect.value;
          break;
        case 'chain':
          if (t && t in b.chainAdd) b.chainAdd[t] += n.effect.value;
          break;
        case 'cooldown':
          if (t === 'artillery') {
            // special cooldown is per-tower; handled via rate-like effect on specials later
            b.rateMul.artillery += n.effect.value * 0.5;
          } else if (t && t in b.abilityCdMul) {
            b.abilityCdMul[t] *= 1 - n.effect.value;
          }
          break;
        case 'economy':
          if (t === 'barrier') {
            b.barrierCostMul *= 1 - n.effect.value;
            break;
          }
          if (t === 'baseUpgrade') {
            b.baseUpgradeCostMul *= 1 - n.effect.value;
            break;
          }
          if (n.effect.value > 0 && n.effect.value < 1) {
            // fractional = early-wave bonus multiplier
            b.earlyWaveBonusMul += n.effect.value;
          } else {
            b.startSupplyBonus += n.effect.value;
          }
          break;
        case 'special':
          if (t === 'rollingDeathBalls') {
            b.rollingBombCount += n.effect.value;
            break;
          }
          if (t === 'rollingDeathSnow') {
            b.rollingBombSnowUnlocked = true;
            b.rollingBombSlowDuration += n.effect.value;
            break;
          }
          if (t === 'barrier') {
            if (n.id.includes('time') || n.desc.includes('duration')) b.barrierDurationMul += n.effect.value;
            else b.barrierHpMul += n.effect.value;
          } else if (t === 'airstrike') {
            b.airstrikeRadiusMul += n.effect.value;
          } else if (t === 'airstrikeFire') {
            b.airstrikeFireDuration += n.effect.value;
          } else if (t === 'emp') {
            b.empDurationMul += n.effect.value;
          } else if (t === 'integrity') {
            b.integrityBonus += n.effect.value;
          } else if (t === 'thermal') {
            b.thermalSplashMul += n.effect.value;
          } else if (t === 'baseDamage') {
            b.baseDamageMul += n.effect.value;
          } else if (t === 'baseRange') {
            b.baseRangeMul += n.effect.value;
          } else if (t === 'symbiosisDamage') {
            b.symbiosisDamageMul += n.effect.value;
          } else if (t?.startsWith('symbiosis:')) {
            b.symbiosisUnlocked[t.slice('symbiosis:'.length)] = true;
          }
          break;
      }
    }
    return b;
  }

  /** Free research for testing / first-time commander starter pack */
  grantStarterIfEmpty(): void {
    if (this.purchased.length === 0 && saveManager.data.researchPoints === 0) {
      // Award 2 starting DATA so research is immediately usable after first mission
      // (actual grant happens after first mission; this is a no-op placeholder)
    }
  }
}

export const researchManager = new ResearchManager();
